import React, { useState, useEffect } from "react";
import { Button, InputNumber, Modal, Select, Space, Switch, Tag, Typography } from "antd";
import StatusTag from "../../../../../components/shared/StatusTag";
import InlineLoading from "../../../../../components/shared/InlineLoading";
import EmptyState from "../../../../../components/shared/EmptyState";
import { BellOutlined, ClockCircleOutlined, DeleteOutlined, DragOutlined, InfoCircleOutlined, MailOutlined, MessageOutlined, PlusOutlined } from "@ant-design/icons";
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
	AppointmentTypeReminder,
	createAppointmentTypeReminder,
	deleteAppointmentTypeReminder,
	fetchAppointmentTypeReminders,
	fetchAvailableReminderTemplates,
	reorderAppointmentTypeReminders,
	toggleAppointmentTypeReminderActive,
} from "../../../../../api/healthcare/Appointment/AppointmentReminderApi";
import { useNotifications } from "../../../../../context/NotificationContext";

const { Title,Text } = Typography;
const { confirm } = Modal;

interface ReminderWithTemplate extends AppointmentTypeReminder {
	template_name?: string;
	template_type?: string;
	template_subject?: string;
}

const SortableReminderCard: React.FC<{
	reminder: ReminderWithTemplate;
	onToggleActive: (id: number) => void;
	onDelete: (id: number) => void;
	disabled: boolean;
}> = ({ reminder, onToggleActive, onDelete, disabled }) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: reminder.id,
	});

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	const getLeadDescription = () => {
		const parts: string[] = [];
		if (reminder.remind_days_before) {
			parts.push(`${reminder.remind_days_before} day${reminder.remind_days_before > 1 ? "s" : ""}`);
		}
		if (reminder.remind_hours_before) {
			parts.push(`${reminder.remind_hours_before} hour${reminder.remind_hours_before > 1 ? "s" : ""}`);
		}
		return parts.join(" and ") || "Not set";
	};

	return (
		<div ref={setNodeRef} style={style}>
			<div
				style={{
					marginBottom: 12,
					borderRadius: 8,
					padding: "12px 16px",
					background: "var(--mg-bg-elevated)",
					border: reminder.is_active ? "1px solid var(--mg-primary-border)" : "1px solid var(--mg-border)",
				}}
			>
				<div style={{ display: "flex", alignItems: "start", gap: 12 }}>
					<div {...attributes} {...listeners} style={{ cursor: disabled ? "not-allowed" : "grab", padding: "4px 0", opacity: disabled ? 0.5 : 1 }}>
						<DragOutlined style={{ fontSize: 16, color: "var(--mg-text-secondary)" }} />
					</div>

					<div style={{ flex: 1 }}>
						<div style={{ marginBottom: 8 }}>
							<Text strong>{reminder.template_name || `Template #${reminder.notification_template_id}`}</Text>
							<Space style={{ marginLeft: 8 }}>
							<StatusTag status={reminder.is_active ? "active" : "inactive"} />
								{reminder.template_type === "email" && (
									<Tag icon={<MailOutlined />} color="blue">
										Email
									</Tag>
								)}
								{reminder.template_type === "sms" && (
									<Tag icon={<MessageOutlined />} color="green">
										SMS
									</Tag>
								)}
								{reminder.template_type === "both" && (
									<Tag icon={<MailOutlined />} color="purple">
										Both
									</Tag>
								)}
							</Space>
						</div>

						{reminder.template_subject && (
							<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
								{reminder.template_subject}
							</Text>
						)}

						<div style={{ display: "flex", alignItems: "center", gap: 16 }}>
							<div style={{ display: "flex", alignItems: "center", gap: 4 }}>
								<ClockCircleOutlined style={{ fontSize: 12, color: "var(--mg-primary)" }} />
								<Text type="secondary" style={{ fontSize: 11 }}>
									Send {getLeadDescription()} before appointment
								</Text>
							</div>
							<Text type="secondary" style={{ fontSize: 11 }}>
								Order: #{reminder.sort_order + 1}
							</Text>
						</div>
					</div>

					<Space direction="vertical" size="small" style={{ alignItems: "flex-end" }}>
						<Switch size="small" checked={reminder.is_active} onChange={() => onToggleActive(reminder.id)} disabled={disabled} />
						<Button danger size="small" icon={<DeleteOutlined />} onClick={() => onDelete(reminder.id)} disabled={disabled}>
							Remove
						</Button>
					</Space>
				</div>
			</div>
		</div>
	);
};

interface ReminderNotificationsTabProps {
	appointmentTypeId: number;
}

const ReminderNotificationsTab: React.FC<ReminderNotificationsTabProps> = ({ appointmentTypeId }) => {
	const [reminders, setReminders] = useState<ReminderWithTemplate[]>([]);
	const [loading, setLoading] = useState(false);
	const [modalVisible, setModalVisible] = useState(false);
	const [availableTemplates, setAvailableTemplates] = useState<any[]>([]);
	const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
	const [remindDays, setRemindDays] = useState<number | undefined>(1);
	const [remindHours, setRemindHours] = useState<number | undefined>(0);

	const { showSuccess, showError } = useNotifications();

	const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

	useEffect(() => {
		loadReminders();
		loadAvailableTemplates();
	}, [appointmentTypeId]);

	const loadReminders = async () => {
		setLoading(true);
		try {
			const data = await fetchAppointmentTypeReminders(appointmentTypeId);
			const templates = await fetchAvailableReminderTemplates();

			const enriched = data.map((r) => {
				const t = templates.find((tpl: any) => tpl.id === r.notification_template_id);
				return {
					...r,
					template_name: t?.name,
					template_type: t?.type,
					template_subject: t?.subject,
				};
			});

			setReminders(enriched.sort((a, b) => a.sort_order - b.sort_order));
		} catch (err: any) {
			showError(err.response?.data?.message || "Failed to load reminder notifications");
		} finally {
			setLoading(false);
		}
	};

	const loadAvailableTemplates = async () => {
		try {
			const templates = await fetchAvailableReminderTemplates();
			setAvailableTemplates(Array.isArray(templates) ? templates : []);
		} catch {
			showError("Failed to load reminder templates");
		}
	};

	const handleOpenModal = () => {
		setSelectedTemplateId(null);
		setRemindDays(1);
		setRemindHours(0);
		setModalVisible(true);
	};

	const handleCreate = async () => {
		if (!selectedTemplateId) {
			showError("Please select a template");
			return;
		}
		if (!remindDays && !remindHours) {
			showError("Please specify reminder days or hours");
			return;
		}

		setLoading(true);
		try {
			await createAppointmentTypeReminder(appointmentTypeId, {
				notification_template_id: selectedTemplateId,
				remind_days_before: remindDays,
				remind_hours_before: remindHours,
				is_active: true,
				sort_order: reminders.length,
			});
			showSuccess("Reminder notification added successfully");
			setModalVisible(false);
			await loadReminders();
		} catch (err: any) {
			showError(err.response?.data?.message || "Failed to create reminder notification");
		} finally {
			setLoading(false);
		}
	};

	const handleToggleActive = async (id: number) => {
		setLoading(true);
		try {
			await toggleAppointmentTypeReminderActive(appointmentTypeId, id);
			showSuccess("Reminder notification updated");
			await loadReminders();
		} catch (err: any) {
			showError(err.response?.data?.message || "Failed to update reminder notification");
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = (id: number) => {
		confirm({
			title: "Remove Reminder Notification?",
			content: "Are you sure you want to remove this reminder notification?",
			okText: "Remove",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				setLoading(true);
				try {
					await deleteAppointmentTypeReminder(appointmentTypeId, id);
					showSuccess("Reminder notification removed successfully");
					await loadReminders();
				} catch (err: any) {
					showError(err.response?.data?.message || "Failed to remove reminder notification");
				} finally {
					setLoading(false);
				}
			},
		});
	};

	const handleDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;

		const oldIdx = reminders.findIndex((r) => r.id === active.id);
		const newIdx = reminders.findIndex((r) => r.id === over.id);
		const reordered = arrayMove(reminders, oldIdx, newIdx).map((r, i) => ({
			...r,
			sort_order: i,
		}));

		setReminders(reordered);

		try {
			await reorderAppointmentTypeReminders(appointmentTypeId, {
				reminders: reordered.map((r) => ({ id: r.id, sort_order: r.sort_order })),
			});
			showSuccess("Reminder order updated");
		} catch {
			showError("Failed to reorder reminders");
			await loadReminders();
		}
	};

	const getUnassignedTemplates = () => {
		const assigned = new Set(reminders.map((r) => r.notification_template_id));
		return availableTemplates.filter((t) => !assigned.has(t.id));
	};

	const previewDelay = () => {
		const parts: string[] = [];
		if (remindDays) parts.push(`${remindDays} day${remindDays > 1 ? "s" : ""}`);
		if (remindHours) parts.push(`${remindHours} hour${remindHours > 1 ? "s" : ""}`);
		return parts.length ? parts.join(" and ") + " before appointment" : "immediately before appointment";
	};

	return (
		<div>
			<div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				<div>
					<Title level={5} style={{ margin: 0, marginBottom: 4 }}>
						Reminder Notifications
					</Title>
					<Text type="secondary" style={{ fontSize: 12 }}>
						Automatically notify patients before their appointment
					</Text>
				</div>
				<Button type="primary" icon={<PlusOutlined />} size="small" onClick={handleOpenModal} disabled={loading}>
					Add Reminder
				</Button>
			</div>

			{loading ? (
				<InlineLoading minHeight={160} message="Loading reminder notifications…" />
			) : reminders.length === 0 ? (
				<EmptyState title="No reminder notifications configured" description='Click "Add Reminder" to notify patients before their appointment' />
			) : (
				<>
					<div style={{ marginBottom: 12, padding: 8, borderRadius: 6, background: "var(--mg-bg-subtle)", border: "1px solid var(--mg-border)" }}>
						<Space size="small">
							<InfoCircleOutlined style={{ color: "var(--mg-primary)" }} />
							<Text style={{ fontSize: 12 }}>Drag and drop to reorder. Reminders are sent in the order shown.</Text>
						</Space>
					</div>

					<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
						<SortableContext items={reminders.map((r) => r.id)} strategy={verticalListSortingStrategy}>
							{reminders.map((reminder) => (
								<SortableReminderCard key={reminder.id} reminder={reminder} onToggleActive={handleToggleActive} onDelete={handleDelete} disabled={loading} />
							))}
						</SortableContext>
					</DndContext>
				</>
			)}

			<Modal
				title="Add Reminder Notification"
				open={modalVisible}
				onCancel={() => setModalVisible(false)}
				onOk={handleCreate}
				okText="Add"
				okButtonProps={{
					disabled: !selectedTemplateId || (!remindDays && !remindHours),
					loading,
				}}
				width={600}
			>
				<Space direction="vertical" style={{ width: "100%" }} size="middle">
					<div>
						<label style={{ display: "block", marginBottom: 8, fontSize: 13, fontWeight: 500 }}>Notification Template</label>
						<Select
							placeholder="Select a reminder template"
							value={selectedTemplateId}
							onChange={setSelectedTemplateId}
							style={{ width: "100%" }}
							showSearch
							filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
							options={getUnassignedTemplates().map((t) => ({
								label: t.name,
								value: t.id,
							}))}
						/>
						{getUnassignedTemplates().length === 0 && (
							<Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
								All available reminder templates have been assigned. Create new templates in Settings.
							</Text>
						)}
					</div>

					<div>
						<label style={{ display: "block", marginBottom: 8, fontSize: 13, fontWeight: 500 }}>Send Reminder Before Appointment</label>
						<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
							<div>
								<label style={{ display: "block", marginBottom: 4, fontSize: 12 }}>Days</label>
								<InputNumber min={0} max={365} value={remindDays} onChange={(v) => setRemindDays(v ? Number(v) : undefined)} style={{ width: "100%" }} placeholder="e.g., 1" />
							</div>
							<div>
								<label style={{ display: "block", marginBottom: 4, fontSize: 12 }}>Hours</label>
								<InputNumber min={0} max={168} value={remindHours} onChange={(v) => setRemindHours(v ? Number(v) : undefined)} style={{ width: "100%" }} placeholder="e.g., 2" />
							</div>
						</div>
						<Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
							The reminder will be sent this long before the appointment starts
						</Text>
					</div>

					{selectedTemplateId && (
						<div style={{ padding: 12, borderRadius: 6, background: "var(--mg-bg-subtle)", border: "1px solid var(--mg-border)" }}>
							<Text strong style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
								Preview:
							</Text>
							<Text style={{ fontSize: 12 }}>{availableTemplates.find((t) => t.id === selectedTemplateId)?.name}</Text>
							<br />
							<Text type="secondary" style={{ fontSize: 11 }}>
								Will be sent {previewDelay()}
							</Text>
						</div>
					)}
				</Space>
			</Modal>
		</div>
	);
};

export default ReminderNotificationsTab;
